import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterValues } from "../../../data-model/filterValues";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { buildOncologySubmissionFilterPayload } from "../utils/buildOncologySubmissionFilterPayload";
import {
  OncologySubmissionFilterPayload,
  useFilterPayload,
} from "./useFilterPayload";
import { OncologySubmissionTableState } from "./useOncologySubmissionTableData";

export interface UseNextPageDataFetchReturn {
  endIndex: number;
  setEndIndex: React.Dispatch<React.SetStateAction<number>>;
}

export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  oncologySubmissionState: OncologySubmissionTableState,
  setOncologySubmissionState: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
): UseNextPageDataFetchReturn => {
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState<number>(0);

  const lastRequestedPageRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setOncologySubmissionStateRef = useRef(setOncologySubmissionState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setOncologySubmissionStateRef.current = setOncologySubmissionState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setOncologySubmissionState,
    setTableData,
    setTableDataForContext,
  ]);

  const validFiltersKey = JSON.stringify(validFilters);
  useEffect(() => {
    lastRequestedPageRef.current = 0;
  }, [validFiltersKey, searchTerm, dateFromStart, dateFromEnd]);

  const shouldFetchMoreData = useMemo(() => {
    const currentPageRowCount = oncologySubmissionState.data?.row_count ?? 0;
    const currentPage = oncologySubmissionState.data?.page ?? 1;
    const totalPages = oncologySubmissionState.data?.total_pages ?? 1;
    const nextPageToFetch = currentPage + 1;

    const previousPagesRows = (currentPage - 1) * pageSize;
    const cumulativeRowsLoaded = previousPagesRows + currentPageRowCount;

    return (
      endIndex >= cumulativeRowsLoaded - 1 &&
      currentPage < totalPages &&
      !oncologySubmissionState.loading &&
      !oncologySubmissionState.showInlineLoader &&
      nextPageToFetch > lastRequestedPageRef.current
    );
  }, [
    endIndex,
    oncologySubmissionState.data?.row_count,
    oncologySubmissionState.data?.page,
    oncologySubmissionState.data?.total_pages,
    oncologySubmissionState.loading,
    oncologySubmissionState.showInlineLoader,
    pageSize,
  ]);

  const payloadFinal = useMemo<OncologySubmissionFilterPayload>(() => {
    if (filterPayload === null) {
      return buildOncologySubmissionFilterPayload(
        validFilters,
        searchTerm,
        dateFromStart,
        dateFromEnd,
        pageSize,
      );
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    dateFromStart,
    dateFromEnd,
    searchTerm,
    pageSize,
  ]);

  const payloadFinalWithPage = useMemo<OncologySubmissionFilterPayload>(() => {
    return {
      ...payloadFinal,
      page: (oncologySubmissionState.data?.page ?? 0) + 1,
      request_id: uuidv4(),
    };
  }, [payloadFinal, oncologySubmissionState.data?.page]);

  const currentPage = oncologySubmissionState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    if (!isMountedRef.current) return;

    const nextPage = currentPage + 1;
    lastRequestedPageRef.current = nextPage;

    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      setOncologySubmissionStateRef.current,
      setTableDataRef.current,
      isMountedRef,
    );
    setTableDataForContextRef.current(TableNames.ONCOLOGY_SUBMISSION_ORDERS, {
      lastBatchFetched: nextPage,
    });
  }, [payloadFinalWithPage, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
